// Mapping from EventsPlayer.js: 1=right foot, 2=left foot, 3=header, 4=body.
// `lid` is the lexicon id; components render l(lid) so labels translate.
export const BODY_PARTS = [
  { id: null, label: 'All', lid: 1837 },
  { id: 2, label: 'Left foot', lid: 1613 },
  { id: 1, label: 'Right foot', lid: 1614 },
  { id: 3, label: 'Header', lid: 1615 },
  { id: 4, label: 'Body', lid: 1616 },
];

// Returns the lexicon id for a body-part id (resolve with l() at render).
export const bodyPartLexId = (id) =>
  BODY_PARTS.find((b) => b.id === id)?.lid ?? 1837;

// Resolve a team-logo value from markers_search to an absolute URL. That
// endpoint returns a bare path: newer crests live in Laravel storage and are
// served from the API host at /storage/teams/<hash>.png (api-v1), while older
// ones sit under the api-v0 /media path (e.g. pics/teams/X.png). Blindly
// prefixing every value with api-v0/media 404s the newer storage crests, which
// is why they showed as broken images (SMRTSTATS-361 follow-up). Already-
// absolute URLs (as some resources return) pass through untouched.
export const teamLogoUrl = (logo) => {
  if (!logo) return null;
  if (/^https?:\/\//i.test(logo)) return logo;
  const path = String(logo).replace(/^\/+/, '');
  return path.startsWith('teams/')
    ? `https://api-v1.smrtstats.com/storage/${path}`
    : `https://api-v0.smrtstats.com/media/${path}`;
};

// Derivative params (percentages, +/- splits, per-minute rates) reuse the same
// underlying marker IDs as their base counterparts, so we skip them when
// computing the union of marker IDs to fetch — keeps the request small and
// avoids duplicate clips for the same moment.
const DERIVATIVE_SUFFIX_RE = /(\s[+-]|,\s*%|%|\/m(in)?)\s*$/;

// Allow-list of "+/-" params that have NO base counterpart in the SmrtStats
// taxonomy and must be force-included despite the suffix regex. Without this,
// the Events page silently drops actual goal markers because "Goals +" (id 2)
// and "Goals Penalties +" (id 3) match the derivative pattern but there is no
// plain "Goals" parameter to fall back to. SMRTSTATS-297 follow-up.
const FORCE_INCLUDE_PARAM_IDS = new Set([2, 3]);

export const isBaseEventParam = (param) => {
  const id = Number(param?.parameter_id ?? param?.id);
  if (FORCE_INCLUDE_PARAM_IDS.has(id)) return true;
  const name = String(param?.parameter_name || param?.name || '');
  return !DERIVATIVE_SUFFIX_RE.test(name);
};

export const paramsWithMarkers = (matchRow) => {
  if (!matchRow || !Array.isArray(matchRow.parameters)) return [];
  return matchRow.parameters.filter(
    (p) => Array.isArray(p.markers) && p.markers.length > 0 && isBaseEventParam(p),
  );
};

// Collect the union of marker IDs across all base params on a match.
// Used as the input to /player/markers_search.
export const collectMarkerIds = (matchRow) => {
  const ids = [];
  paramsWithMarkers(matchRow).forEach((p) => {
    (p.markers || []).forEach((id) => {
      const n = Number(id);
      if (Number.isFinite(n)) ids.push(n);
    });
  });
  return Array.from(new Set(ids));
};

// Build a marker-id → param-id-set map so the FE can filter the events list
// by parameter category (Phase 3 filter UX).
export const buildMarkerParamIndex = (matchRow) => {
  const index = new Map();
  paramsWithMarkers(matchRow).forEach((p) => {
    const paramId = Number(p.parameter_id ?? p.id);
    (p.markers || []).forEach((id) => {
      const n = Number(id);
      if (!Number.isFinite(n)) return;
      if (!index.has(n)) index.set(n, new Set());
      index.get(n).add(paramId);
    });
  });
  return index;
};

// Hydrated marker → event row, deduping by marker.id.
// `markers` is the array returned by /player/markers_search (response.events).
//
// Order: matches sorted by date desc (most recent first), then events within
// a match sorted by second asc (chronological). This keeps the flat events
// array aligned with what groupEventsByMatch produces visually, so Prev/Next
// in EventsVideo walks the same order the user sees on screen — no more
// jumping between matches mid-playlist.
export const buildEventsFromMarkers = (markers) => {
  if (!Array.isArray(markers)) return [];
  const seen = new Set();
  const events = [];
  markers.forEach((m) => {
    const id = m?.id;
    if (id == null || seen.has(id)) return;
    const second = Number(m.second);
    if (!Number.isFinite(second)) return;
    seen.add(id);
    events.push({
      key: String(id),
      markerId: id,
      paramName: m.action ?? 'Event',
      second,
      videoLink: m.video ?? null,
      coordX: m.coord_x != null ? Number(m.coord_x) : null,
      coordY: m.coord_y != null ? Number(m.coord_y) : null,
      match: m.match ? {
        id: m.match.id,
        // home_team / away_team can be either a string or a {id,name,...}
        // object depending on the staff resource version. Coerce to name.
        homeTeam: typeof m.match.home_team === 'string'
          ? m.match.home_team
          : m.match.home_team?.name ?? null,
        awayTeam: typeof m.match.away_team === 'string'
          ? m.match.away_team
          : m.match.away_team?.name ?? null,
        // Markers_search returns team logos as bare paths; resolve storage vs
        // media so newer crests don't 404 (see teamLogoUrl).
        homeTeamLogo: typeof m.match.home_team === 'object'
          ? teamLogoUrl(m.match.home_team?.logo)
          : null,
        awayTeamLogo: typeof m.match.away_team === 'object'
          ? teamLogoUrl(m.match.away_team?.logo)
          : null,
        score: m.match.score ?? null,
        date: m.match.date ?? null,
        competition: typeof m.match.competition === 'string'
          ? m.match.competition
          : m.match.competition?.name ?? null,
      } : null,
    });
  });

  // Sort by match.date desc, then by second asc. Missing dates sort last but
  // stay together so events without a match still cluster in chronological
  // order rather than interleaving with dated matches.
  events.sort((a, b) => {
    const aDate = a.match?.date ? new Date(a.match.date).getTime() : -Infinity;
    const bDate = b.match?.date ? new Date(b.match.date).getTime() : -Infinity;
    if (aDate !== bDate) return bDate - aDate; // desc
    const aMid = a.match?.id ?? 0;
    const bMid = b.match?.id ?? 0;
    if (aMid !== bMid) return aMid - bMid;
    return a.second - b.second;
  });
  return events;
};

// Group events by match.id, preserving the chronological order within each
// group. Returns [{ match, events: [] }, ...] in the order of first appearance.
export const groupEventsByMatch = (events) => {
  const groups = new Map();
  events.forEach((e) => {
    const matchId = e.match?.id ?? '__no_match__';
    if (!groups.has(matchId)) {
      groups.set(matchId, { match: e.match, events: [] });
    }
    groups.get(matchId).events.push(e);
  });
  return Array.from(groups.values());
};

export const formatSeconds = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

export const encodeEventKeys = (keys) => Array.from(keys).join(',');

export const parseEventKeys = (raw) => {
  if (!raw || typeof raw !== 'string') return new Set();
  return new Set(raw.split(',').map((s) => s.trim()).filter(Boolean));
};
